// Data shaping for the model-wise claims chart. Pure: no React, no I/O.
// Every figure is derived from the /forecast-claims/ response; nothing is recalculated.

export const CURRENT_DATE = new Date();
export const ALL_MODELS = '__ALL__';
export const ALL_MANUFACTURERS = '__ALL_MANUFACTURERS__';
export const OTHER_GROUP = '__OTHER__';   // forecast models missing from the catalogue

// Validated 8-slot categorical palette (light surface). Colours follow the entity in a fixed order
// (manufacturer order in the catalogue, or model order within a manufacturer), so filtering never
// repaints a series. Predicted bars use the same hue at reduced opacity.
const SERIES_PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
const OTHER_COLOR = '#777777';
export const PREDICTED_OPACITY = 0.45;

export const colorsFor = (seriesKeys) => {
  const colors = {};
  seriesKeys.forEach((key, index) => {
    colors[key] = key === OTHER_GROUP || index >= SERIES_PALETTE.length ? OTHER_COLOR : SERIES_PALETTE[index];
  });
  return colors;
};

const addParts = (target, source) => {
  Object.entries(source || {}).forEach(([part, count]) => {
    target[part] = (target[part] || 0) + (count || 0);
  });
};

// Presentation only: sums the endpoint's per-model daily values into one entry per group (e.g. per manufacturer).
// Every figure is a sum of values returned by /forecast-claims/; nothing is recalculated.
export const groupDailyData = (raw, groupOf) => {
  const grouped = {};
  Object.entries(raw || {}).forEach(([day, models]) => {
    grouped[day] = {};
    Object.entries(models || {}).forEach(([modelKey, m]) => {
      const group = groupOf(modelKey);
      if (!grouped[day][group]) {
        grouped[day][group] = { total_actual_claims: 0, total_predicted_claims: 0, actual_parts: {}, predicted_parts: {} };
      }
      const g = grouped[day][group];
      g.total_actual_claims += m.total_actual_claims || 0;
      g.total_predicted_claims += m.total_predicted_claims || 0;
      addParts(g.actual_parts, m.actual_parts);
      addParts(g.predicted_parts, m.predicted_parts);
    });
  });
  return grouped;
};

const toISO = (d) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString().split('T')[0];
};
const monthKey = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short' });

export const processTimeSeriesData = (raw, period, modelNames) => {
  if (!raw || modelNames.length === 0) return { chartData: [], bucketPartsData: {} };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Boundary for Actual vs Predicted (1st day of the current month)
  const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const buckets = [];
  if (period === 'WTD') {
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - startOfWeek.getDay());
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      buckets.push({
        label: day.toLocaleDateString('en-US', { weekday: 'short' }),
        bucketKey: toISO(day),
        dateObj: day,
      });
    }
  } else if (period === 'MTD') {
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(today.getFullYear(), today.getMonth(), i);
      buckets.push({ label: String(i), bucketKey: toISO(day), dateObj: day });
    }
  } else {
    for (let m = 0; m < 12; m++) {
      const day = new Date(today.getFullYear(), m, 1);
      buckets.push({
        label: day.toLocaleDateString('en-US', { month: 'short' }),
        bucketKey: monthKey(day),
        dateObj: day
      });
    }
  }

  const totalsByBucket = {};
  const bucketPartsData = {};

  Object.keys(raw).forEach((dateStr) => {
    const entryDate = new Date(dateStr);
    entryDate.setHours(0, 0, 0, 0);

    const keyForTotals = (period === 'YTD') ? monthKey(entryDate) : toISO(entryDate);

    const bucketExists = buckets.some(b => b.bucketKey === keyForTotals);
    if (!bucketExists) return;

    if (!totalsByBucket[keyForTotals]) totalsByBucket[keyForTotals] = {};
    if (!bucketPartsData[keyForTotals]) bucketPartsData[keyForTotals] = {};

    const modelsForDate = raw[dateStr] || {};
    Object.keys(modelsForDate).forEach((model) => {
      const m = modelsForDate[model];

      if (!totalsByBucket[keyForTotals][model]) {
        totalsByBucket[keyForTotals][model] = { total_actual_claims: 0, total_predicted_claims: 0 };
      }
      totalsByBucket[keyForTotals][model].total_actual_claims += (m.total_actual_claims || 0);
      totalsByBucket[keyForTotals][model].total_predicted_claims += (m.total_predicted_claims || 0);

      if (!bucketPartsData[keyForTotals][model]) {
        bucketPartsData[keyForTotals][model] = { actual_parts: {}, predicted_parts: {} };
      }

      // Actuals are only aggregated if the entry date is before the start of the current month
      if (entryDate < startOfCurrentMonth) {
        Object.entries(m.actual_parts || {}).forEach(([part, count]) => {
          bucketPartsData[keyForTotals][model].actual_parts[part] =
            (bucketPartsData[keyForTotals][model].actual_parts[part] || 0) + (count || 0);
        });
      }

      Object.entries(m.predicted_parts || {}).forEach(([part, count]) => {
        bucketPartsData[keyForTotals][model].predicted_parts[part] =
          (bucketPartsData[keyForTotals][model].predicted_parts[part] || 0) + (count || 0);
      });
    });
  });

  const chartData = buckets.map(({ label, bucketKey, dateObj }) => {
    const row = { name: label, bucketKey, isFuture: false };
    const models = totalsByBucket[bucketKey] || {};

    modelNames.forEach((model) => {
      const actualKey = `${model}_actual`;
      const predictedKey = `${model}_predicted`;
      const totals = models[model] || null;

      if (period === 'YTD') {
        const nowMonthIndex = today.getMonth();
        const rowMonthIndex = dateObj.getMonth();
        const isFutureMonth = rowMonthIndex >= nowMonthIndex;
        if (isFutureMonth) row.isFuture = true;
        row[actualKey] = isFutureMonth ? 0 : (totals?.total_actual_claims || 0);
        row[predictedKey] = totals?.total_predicted_claims || 0;
      } else {
        const isFutureDay = new Date(dateObj) >= startOfCurrentMonth;
        if (isFutureDay) row.isFuture = true;
        row[actualKey] = isFutureDay ? 0 : (totals?.total_actual_claims || 0);
        row[predictedKey] = totals?.total_predicted_claims || 0;
      }
    });

    return row;
  });

  return { chartData, bucketPartsData };
};
